import { QuizTopicPageTemplate } from "@/components/QuizTopicPageTemplate";

const tocItems = [
  { id: "why-music-quizzes", title: "Why Music Quizzes?" },
  { id: "genre-quizzes", title: "Genre-Specific Quizzes" },
  { id: "song-matching", title: "Song & Artist Matching" },
  { id: "music-history", title: "Music History Trivia" },
];

const quizResources = [
  { name: "Britannica Music Quiz", url: "https://www.britannica.com/quiz/music-quiz", description: "Music from around the world" },
  { name: "Rock 'n' Roll Quiz", url: "https://www.britannica.com/quiz/rock-music-and-rock-n-roll", description: "Rock music history" },
  { name: "Match Song to Band", url: "https://play.howstuffworks.com/quiz/can-you-match-the-song-the-band", description: "Match songs to bands" },
  { name: "EDM Quiz", url: "https://play.howstuffworks.com/quiz/electronic-dance-music-quiz", description: "Electronic dance music trivia" },
  { name: "Progressive Rock Quiz", url: "https://play.howstuffworks.com/quiz/progressive-rock-quiz", description: "From Yes to Pink Floyd" },
];

const relatedTopics = [
  { name: "Pop Culture Quizzes", path: "/quizzes/pop-culture-quizzes/", description: "Movies, TV, and more" },
  { name: "Fun Quizzes", path: "/quizzes/fun-quizzes-to-take/", description: "Entertainment quizzes" },
  { name: "Trivia Quizzes", path: "/quizzes/trivia-quizzes/", description: "General knowledge" },
];

const MusicQuizzes = () => {
  return (
    <QuizTopicPageTemplate
      title="Music Quizzes: Test Your Music Knowledge"
      metaTitle="Music Quizzes | Song Trivia & Artist Tests 2025"
      metaDescription="Challenge your music knowledge with quizzes on rock, pop, EDM, and more. Match songs to artists, identify decades, and prove you're a true music expert."
      keywords="music quizzes, song trivia, artist quiz, rock music quiz, pop music trivia, music history quiz, band quizzes"
      canonicalUrl="/quizzes/music-quizzes/"
      category="Music"
      publishDate="January 12, 2025"
      readTime="5 min read"
      tocItems={tocItems}
      leadParagraph="Music is a universal language, but how well do you speak it? From classic rock legends to today's chart-toppers, music quizzes test your knowledge of songs, artists, genres, and musical history."
      quizResources={quizResources}
      relatedTopics={relatedTopics}
    >
      <section id="why-music-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Why <span className="text-gradient">Music Quizzes?</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Whether you're a casual listener or a dedicated audiophile, music quizzes offer a fun way to test your knowledge and discover new facts about your favorite artists and songs.
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Test genre knowledge:</strong> Rock, pop, hip-hop, classical, and more</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Learn music history:</strong> Evolution of styles and movements</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Discover new artists:</strong> Find music you might have missed</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Challenge friends:</strong> Settle debates about music knowledge</span>
          </li>
        </ul>
      </section>

      <section id="genre-quizzes" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Genre-Specific <span className="text-gradient">Quizzes</span>
        </h2>
        
        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Rock & Classic Rock</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          From Pink Floyd to Rush, rock music quizzes cover legendary bands, iconic albums, and music history. Britannica's <a href="https://www.britannica.com/quiz/rock-music-and-rock-n-roll" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Rock 'n' Roll Quiz</a> tests your knowledge of the genre that changed music forever.
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Electronic Dance Music (EDM)</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          <a href="https://play.howstuffworks.com/quiz/electronic-dance-music-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">"Here Comes the Drop" EDM Quiz</a> tests your knowledge of EDM subgenres, famous DJs, and electronic music history. Know your house from your dubstep? Prove it.
        </p>

        <h3 className="text-2xl font-display font-semibold mt-8 mb-4">Progressive Rock</h3>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Yes, Genesis, Pink Floyd, Rush—progressive rock took music to new heights. <a href="https://play.howstuffworks.com/quiz/progressive-rock-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">The Progressive Rock Quiz</a> tests your knowledge of complex compositions and concept albums.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <a href="https://www.britannica.com/quiz/music-quiz" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">World Music</h4>
            <p className="text-sm text-muted-foreground">Music from around the globe</p>
          </a>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Hip-Hop & Rap</h4>
            <p className="text-sm text-muted-foreground">From old school to modern</p>
          </div>
          <a href="https://www.britannica.com/quiz/music-composers-fact-or-fiction" target="_blank" rel="noopener noreferrer" className="p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/50 transition-all group">
            <h4 className="font-semibold mb-2 group-hover:text-primary">Classical Music</h4>
            <p className="text-sm text-muted-foreground">Composers and compositions</p>
          </a>
          <div className="p-4 rounded-xl bg-card/50 border border-border/50">
            <h4 className="font-semibold mb-2">Country & Folk</h4>
            <p className="text-sm text-muted-foreground">Legends and hits</p>
          </div>
        </div>
      </section>

      <section id="song-matching" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Song & Artist <span className="text-gradient">Matching</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Some of the most popular music quizzes involve matching songs to their artists or bands. HowStuffWorks offers quizzes like:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong><a href="https://play.howstuffworks.com/quiz/can-you-match-the-song-the-band" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Match the Song to the Band:</a></strong> "Highway to Hell" belongs to...?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong><a href="https://play.howstuffworks.com/quiz/can-you-match-the-hit-song-its-decade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Match the Hit Song to Its Decade:</a></strong> '80s, '90s, or 2000s?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Name That Tune:</strong> Identify songs from brief clips</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          These quizzes test not just whether you know songs, but whether you can place them in context—the right artist, the right era.
        </p>
      </section>

      <section id="music-history" className="mb-12">
        <h2 className="text-3xl font-display font-bold mb-6">
          Music History <span className="text-gradient">Trivia</span>
        </h2>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          For music nerds who want to go deeper, music history quizzes cover:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong><a href="https://www.britannica.com/quiz/whats-in-a-name-music-edition" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Famous nicknames:</a></strong> Who is "The King of Pop"? "The Boss"?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Band formation:</strong> When and where did famous bands start?</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Album covers:</strong> Identify iconic album artwork</span>
          </li>
          <li className="flex gap-3 text-lg text-foreground/90">
            <span className="text-primary font-bold">•</span>
            <span><strong>Record breakers:</strong> Best-selling albums and singles</span>
          </li>
        </ul>
        <p className="text-lg leading-relaxed text-foreground/90 mb-6">
          Britannica's <a href="https://www.britannica.com/quiz/sound-check-musical-vocabulary-quiz" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Sound Check: Musical Vocabulary Quiz</a> even tests your knowledge of musical terms and theory—perfect for musicians and music students.
        </p>
      </section>
    </QuizTopicPageTemplate>
  );
};

export default MusicQuizzes;
